import { DownOutlined, FileExcelOutlined, FilePdfOutlined, PrinterOutlined } from "@ant-design/icons";
import { Card, Spin, Alert, Select, Button, DatePicker, Dropdown, Menu } from "antd";
import jsPDF from "jspdf";
import "jspdf-autotable";
import React, { useEffect, useState } from "react";
import * as XLSX from "xlsx";
import axios from "axios";
import { getPartyStatement } from "./account.api";
import "./account.css";

const { RangePicker } = DatePicker;

const formatAmount = (value) => {
  if (value == null || value === "") return "—";
  const num = Number(value);
  if (Number.isNaN(num)) return String(value);
  return num.toLocaleString("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 2 });
};

const PartyStatement = () => {
  const [partyType, setPartyType] = useState("customer");
  const [partyId, setPartyId] = useState(null);
  const [dateRange, setDateRange] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loadingParties, setLoadingParties] = useState(true);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchParties = async () => {
      try {
        setLoadingParties(true);
        const [custRes, supRes] = await Promise.all([
          axios.get("customer?query=all&status=true"),
          axios.get("supplier?query=all&status=true"),
        ]);
        setCustomers(custRes.data?.data ?? custRes.data ?? []);
        setSuppliers(supRes.data?.data ?? supRes.data ?? []);
      } catch (err) {
        console.error("Error loading parties:", err);
        setError("Failed to load customer/supplier list");
      } finally {
        setLoadingParties(false);
      }
    };
    fetchParties();
  }, []);

  const handleGetStatement = async () => {
    if (!partyId) {
      setError("Please select a party");
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const fromDate = dateRange?.[0] ? dateRange[0].format("YYYY-MM-DD") : "";
      const toDate = dateRange?.[1] ? dateRange[1].format("YYYY-MM-DD") : "";
      const result = await getPartyStatement(partyType, partyId, fromDate, toDate);
      setData(result);
    } catch (err) {
      setError(err.response?.data?.error || err.message || "Failed to fetch party statement");
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  const partyOptions = partyType === "customer" ? customers : suppliers;
  const invoiceLabel = partyType === "customer" ? "Invoice No." : "Invoice";
  const displayInvoiceNumber = (inv) => {
    const prefix = inv.prefix;
    const num = inv.invoice_number ?? inv.id;
    if (prefix != null && String(prefix).trim() !== "") return `${prefix}${num}`;
    return num != null ? String(num) : "—";
  };

  const buildStatementRows = () => {
    if (!data?.invoices?.length) {
      return {
        headers: ["Date", invoiceLabel, "Total Amount", "Discount", "Add. Discount", "Paid", "Due"],
        rows: [],
      };
    }
    const headers = ["Date", invoiceLabel, "Total Amount", "Discount", "Add. Discount", "Paid", "Due"];
    const rows = data.invoices.map((inv) => [
      new Date(inv.date).toLocaleDateString(),
      displayInvoiceNumber(inv),
      formatAmount(inv.total_amount),
      formatAmount(inv.total_product_discount),
      formatAmount(inv.discount),
      formatAmount(inv.paid_amount),
      formatAmount(inv.due_amount),
    ]);
    rows.push(["Total Outstanding", "", "", "", "", "", formatAmount(data.totalOutstanding)]);
    return { headers, rows };
  };

  const exportPDF = () => {
    if (!data) return;
    const { headers, rows } = buildStatementRows();
    const doc = new jsPDF({ orientation: "portrait", unit: "pt", format: "a4" });
    doc.setFontSize(12);
    doc.text(`Party Statement - ${data.party?.name || "Party"}`, 40, 30);
    doc.autoTable({
      head: [headers],
      body: rows,
      startY: 40,
      theme: "grid",
      headStyles: { fillColor: [0, 0, 0], fontSize: 9, textColor: [255, 255, 255] },
      bodyStyles: { fontSize: 8 },
    });
    doc.save("party-statement.pdf");
  };

  const exportExcel = () => {
    if (!data) return;
    const { headers, rows } = buildStatementRows();
    const sheetData = [headers, ...rows];
    const ws = XLSX.utils.aoa_to_sheet(sheetData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Party Statement");
    XLSX.writeFile(wb, "party-statement.xlsx");
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="ps-page">
      <Card className="ps-card ps-card-modern" bordered={false}>
        <div className="ps-header erp-no-print">
          <h1 className="ps-main-title">Party Statement</h1>
          {data && (
            <Dropdown
              overlay={
                <Menu>
                  <Menu.Item key="pdf" icon={<FilePdfOutlined />} onClick={exportPDF}>
                    Download PDF
                  </Menu.Item>
                  <Menu.Item key="excel" icon={<FileExcelOutlined />} onClick={exportExcel}>
                    Download Excel
                  </Menu.Item>
                  <Menu.Item key="print" icon={<PrinterOutlined />} onClick={handlePrint}>
                    Print
                  </Menu.Item>
                </Menu>
              }
              trigger={["click"]}
              placement="bottomRight"
            >
              <Button type="primary" size="small" className="ps-export-btn">
                Download / Print <DownOutlined />
              </Button>
            </Dropdown>
          )}
        </div>

        <div className="ps-filters erp-no-print">
          {loadingParties ? (
            <Spin tip="Loading parties..." size="small" />
          ) : (
            <div className="ps-filters-row">
              <div>
                <label className="ps-filter-label">Party type</label>
                <Select
                  value={partyType}
                  onChange={(v) => {
                    setPartyType(v);
                    setPartyId(null);
                    setData(null);
                  }}
                  style={{ width: 140 }}
                  options={[
                    { value: "customer", label: "Customer" },
                    { value: "supplier", label: "Supplier" },
                  ]}
                />
              </div>
              <div>
                <label className="ps-filter-label">Party</label>
                <Select
                  placeholder={`Select ${partyType}`}
                  value={partyId || undefined}
                  onChange={setPartyId}
                  style={{ minWidth: 220 }}
                  showSearch
                  optionFilterProp="label"
                  options={partyOptions.map((p) => ({
                    value: p.id,
                    label: p.name,
                  }))}
                />
              </div>
              <div>
                <label className="ps-filter-label">Date range (optional)</label>
                <RangePicker value={dateRange} onChange={setDateRange} />
              </div>
              <Button type="primary" onClick={handleGetStatement} loading={loading}>
                Get Statement
              </Button>
            </div>
          )}
        </div>

        {error && (
          <div className="ps-alert-wrap erp-no-print">
            <Alert
              message="Error"
              description={error}
              type="error"
              showIcon
              closable
              onClose={() => setError(null)}
            />
          </div>
        )}

        {data && (
          <>
            <div className="ps-print-only-title">
              Party Statement - {data.party?.name || "Party"}
            </div>
            <div className="ps-party-info">
              <strong>{data.party?.name}</strong>
              {data.party?.phone && <span className="ps-party-meta">Phone: {data.party.phone}</span>}
              {data.party?.address && <span className="ps-party-meta">Address: {data.party.address}</span>}
            </div>

            <div className="ps-table-wrap">
              <table className="ps-table">
                <thead>
                  <tr>
                    <th>DATE</th>
                    <th>{invoiceLabel.toUpperCase()}</th>
                    <th className="ps-th-amount">TOTAL AMOUNT</th>
                    <th className="ps-th-amount">DISCOUNT</th>
                    <th className="ps-th-amount">ADD. DISCOUNT</th>
                    <th className="ps-th-amount">PAID</th>
                    <th className="ps-th-amount">DUE</th>
                  </tr>
                </thead>
                <tbody>
                  {data.invoices?.length > 0 ? (
                    <>
                      {data.invoices.map((inv) => (
                        <tr key={inv.id}>
                          <td>{new Date(inv.date).toLocaleDateString()}</td>
                          <td className="ps-invoice">{displayInvoiceNumber(inv)}</td>
                          <td className="ps-amount">{formatAmount(inv.total_amount)}</td>
                          <td className="ps-amount">{formatAmount(inv.total_product_discount)}</td>
                          <td className="ps-amount">{formatAmount(inv.discount)}</td>
                          <td className="ps-amount">{formatAmount(inv.paid_amount)}</td>
                          <td className="ps-amount">{formatAmount(inv.due_amount)}</td>
                        </tr>
                      ))}
                      <tr className="ps-total-row">
                        <td colSpan={6}><strong>Total Outstanding</strong></td>
                        <td className="ps-amount"><strong>{formatAmount(data.totalOutstanding)}</strong></td>
                      </tr>
                    </>
                  ) : (
                    <tr>
                      <td colSpan={7}>No invoices in this period</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="ps-footer">
              <span className="ps-summary">
                {data.party?.name} &nbsp;|&nbsp; {data.invoices?.length ?? 0} invoice(s) &nbsp;|&nbsp; Total Outstanding: {formatAmount(data.totalOutstanding)}
              </span>
            </div>
          </>
        )}
      </Card>
    </div>
  );
};

export default PartyStatement;
